import { TextField } from "@material-ui/core";
import PropTypes from "prop-types";
import { useState } from "react";
import NumberFormat from "react-number-format";
import { inputTextToNumber } from "../../../utils";

interface IProps {
  [x: string]: any;

  /** Label para o input. */
  label?: string;
  /** Máscara para formatar o número informado na propriedade value do MaskField. */
  format?: string;
  /** Caracter que, se informado, vai ser exibido nos espaços não preenchidos da máscara/formato. */
  mask?: string;
  /** Prefixo do valor digitado. */
  prefix?: string;
  /** Sufixo do valor digitado. */
  suffix?: string;
  /** Quantidade de casas decimais aceitadas. */
  decimalScale?: number;
  /** Se true, fixará a quantidade de casas decimais para o valor passado na propriedade decimalScale,
   * ou seja, ele preencherá com zeros (0) até chegar a este valor. */
  fixedDecimalScale?: boolean;
  /** Permite valores negativos. */
  allowNegative?: boolean;
  /** Exibe a máscara com input vazio. */
  allowEmptyFormatting?: boolean;
  /** Valor do campo. */
  value?: string | number | null | undefined;
  /** Função que será chamada ao alterar o valor do campo, passando
   * um objeto que recria um event.target com os valores name e value com primeiro parâmetro e no segundo
   * um objeto com os valores retornados pelo NumberFormat, com e sem máscara. */
  onChange?: (falseEvent, values) => void;
  /** Função que será chamada ao sair do campo, passando o event e o valor convertido para Number. */
  onBlur?: (event, valueFloat: number) => void;
  align?: "right" | "left" | "center";
}

/**
 * ##### Campo para formatação de valores numéricos.
 * Este componente utiliza a biblioteca **react-number-format**.
 * Para ver mais detalhes sobre suas propriedades e métodos acesse: https://github.com/s-yadav/react-number-format.
 *
 * > Além das propriedades descritas abaixo é possível passar também as outras propriedades do componente TextField.
 * @category Components
 * */
const MaskedField = ({
  prefix,
  suffix,
  value,
  onChange,
  onBlur,
  align = "left",
  ...otherProps
}: IProps) => {
  const [ref, setRef] = useState<any>(null);
  otherProps.inputProps = { style: { textAlign: align } };

  const handleChange = (values) => {
    if (onChange)
      onChange(
        { target: { value: values.floatValue, name: otherProps.name } },
        values
      );
  };

  const handleBlur = (ev) => {
    if (onBlur) {
      const valueClean = inputTextToNumber(
        ev.target.value.replace(prefix, "").replace(suffix, "")
      );
      onBlur(ev, valueClean);
    }
  };

  // Tratamento para limpar o campo quando o value for undefined
  if (value === undefined && ref && ref.state.value) {
    ref.state.value = "";
    ref.state.numAsString = "";
  }

  return (
    <NumberFormat
      {...otherProps}
      ref={(el) => setRef(el)}
      value={value}
      onValueChange={handleChange}
      prefix={prefix}
      suffix={suffix}
      thousandSeparator={"."}
      decimalSeparator={","}
      allowedDecimalSeparators={[","]}
      onBlur={handleBlur}
      customInput={TextField}
    />
  );
};

MaskedField.propTypes = {
  label: PropTypes.string,
  mask: PropTypes.string,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  decimalScale: PropTypes.number,
  fixedDecimalScale: PropTypes.bool,
  allowNegative: PropTypes.bool,
  allowEmptyFormatting: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
};

MaskedField.defaultProps = {
  label: "",
  decimalScale: 2,
  fixedDecimalScale: false,
  allowNegative: true,
};

export default MaskedField;
